import {User} from 'constants/UserConstants';
import * as FileBrowserDAO from 'file-browser/FileBrowserDAO';
import {isSchemaTag} from 'folder-items/FileConstants';
import notifications from 'notifications';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import * as ExpandedTableTagUtil from 'schemas/tags/ExpandedTableTagUtil';
import {TagForTableDisplay} from 'schemas/tags/TagValue';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import {readableTime} from 'util/DateUtil';
import * as ExportUtil from 'util/ExportUtil';
import {initiateTextDownload} from 'util/TextToBlobUtil';
import uniqueId from 'util/uniqueId';

import {StudyListingGraphQL} from '../../../graphql-fragments/studies/__generated/StudyListingGraphQL';

interface StudyCSVColumn {
  header: string;
  getter: (study: StudyListingGraphQL) => string;
}

function getBaseColumns(authorsByStudyId: {[studyId: string]: Array<User>}): Array<StudyCSVColumn> {
  return [
    {header: 'Name', getter: (study) => study.name},
    {header: getColumnHeaderFromSortKey(SortKey.SORT_IDENTIFIER_KEY), getter: (study) => study.humanId},
    {
      header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
      getter: (study) => readableTime(study.createdAt, {showTime: false}),
    },
    {
      header: getColumnHeaderFromSortKey(SortKey.SORT_MODIFIED_AT_KEY),
      getter: (study) => readableTime(study.modifiedAt, {showTime: false}),
    },
    {
      header: 'Authors',
      getter: (study) =>
        (authorsByStudyId[study.id] ?? []).map((author) => getPartyDisplayString(author) ?? '').join(', '),
    },
    {header: 'Description', getter: (study) => study.description ?? ''},
    {header: 'Phase', getter: (study) => study.phase},
  ];
}

/**
 * Formats a tag value the way the studies listing displays it (see TagValue). Units are omitted so numeric
 * values stay usable in calculations, matching the backend schema field CSV export.
 */
function tagToCSVValue(tag: TagForTableDisplay): string {
  if (isSchemaTag(tag)) {
    if (tag.dateValue) {
      return readableTime(tag.dateValue, {showTime: false});
    } else if (tag.datetimeValue) {
      return readableTime(tag.datetimeValue, {showTime: true});
    }
  }
  return tag.value || '';
}

export function studiesToCSV(
  studies: Array<StudyListingGraphQL>,
  authorsByStudyId: {[studyId: string]: Array<User>}
): string {
  const baseColumns = getBaseColumns(authorsByStudyId);
  const tagColumns = ExpandedTableTagUtil.listSortedTagColumns(studies);
  const header = [
    ...baseColumns.map((column) => column.header),
    ...tagColumns.map((column) => column.header),
  ];
  const rows = studies.map((study) => [
    ...baseColumns.map((column) => column.getter(study)),
    ...tagColumns.map((tagColumn) =>
      ExpandedTableTagUtil.getTagsForColumn({tags: study.tags, tagColumn})
        .map((tag) => tagToCSVValue(tag))
        .join(', ')
    ),
  ]);
  return ExportUtil.formatRows([header, ...rows], ',');
}

export async function exportStudiesToCSV(studies: Array<StudyListingGraphQL>): Promise<void> {
  const notificationId = uniqueId('export-studies-to-csv-');
  notifications.info('Exporting...', {id: notificationId, duration: 0});
  await FileBrowserDAO.bulkGetAuthors(studies.map((study) => study.id))
    .then((authorsByStudyId) => {
      initiateTextDownload(studiesToCSV(studies, authorsByStudyId), 'studies.csv');
      notifications.success('Export complete. Downloading...', {id: notificationId});
    })
    .catch((error) => {
      notifications.notifyError(error, {id: notificationId});
    });
}
